import { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../../lib/supabase";
import { useThemeMode } from "../../../theme/ThemeModeProvider";
import { chatRailTokens } from "../tokens";
import { JsonResultRenderer } from "./toolRenderers/JsonResultRenderer";

function fmtRemaining(ms: number): { text: string; danger: boolean; expired: boolean } {
  if (ms <= 0) return { text: "expired", danger: true, expired: true };
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return {
    text: `${m}:${s.toString().padStart(2, "0")}`,
    danger: total < 60,
    expired: false,
  };
}

interface ApprovalCardProps {
  approvalId: string;
  runId: string;
  expiresAt: string;
  rationale: string | null;
  proposedInput: unknown;
  onOpenRun?: (runId: string) => void;
}

export function ApprovalCard({
  approvalId,
  runId,
  expiresAt,
  rationale,
  proposedInput,
  onOpenRun,
}: ApprovalCardProps) {
  const { mode } = useThemeMode();
  const T = chatRailTokens(mode);
  const qc = useQueryClient();
  const [now, setNow] = useState(() => Date.now());
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const { data: signature } = useQuery({
    queryKey: ["approval-signature", approvalId],
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase
        .from("approvals_view")
        .select("signature")
        .eq("id", approvalId)
        .single();
      if (error) throw error;
      const s = (data as { signature?: unknown } | null)?.signature;
      return typeof s === "string" ? s : null;
    },
  });

  const remaining = useMemo(
    () => fmtRemaining(new Date(expiresAt).getTime() - now),
    [expiresAt, now],
  );

  const approve = useMutation({
    mutationFn: async () => {
      if (!signature) throw new Error("Signature not loaded");
      const { error } = await supabase.schema("agent").rpc("approve_action", {
        p_approval_id: approvalId,
        p_signature: signature,
        p_user_agent: navigator.userAgent,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversation-approvals"] });
      qc.invalidateQueries({ queryKey: ["approvals"] });
    },
  });

  const reject = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.schema("agent").rpc("reject_action", {
        p_approval_id: approvalId,
        p_reason: reason || undefined,
        p_user_agent: navigator.userAgent,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setShowReject(false);
      setReason("");
      qc.invalidateQueries({ queryKey: ["conversation-approvals"] });
      qc.invalidateQueries({ queryKey: ["approvals"] });
    },
  });

  return (
    <Box
      sx={{
        bgcolor: T.confirmSoft,
        border: `1px solid ${T.confirmBorder}`,
        borderRadius: `${T.r_md}px`,
        p: 1.25,
        fontFamily: T.sans,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
        <Typography sx={{ color: T.confirm, fontSize: T.fs_label, fontWeight: 600, flex: 1 }}>
          Approval required
        </Typography>
        <Typography
          sx={{
            color: remaining.danger ? T.failed : T.textDim,
            fontSize: T.fs_micro,
            fontFamily: T.mono,
          }}
        >
          {remaining.text}
        </Typography>
        {onOpenRun && (
          <Box
            component="button"
            type="button"
            onClick={() => onOpenRun(runId)}
            sx={{
              border: "none",
              bgcolor: "transparent",
              cursor: "pointer",
              color: T.accent,
              fontSize: T.fs_micro,
              p: 0,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            details
          </Box>
        )}
      </Stack>

      {rationale && (
        <Typography sx={{ color: T.text, fontSize: T.fs_body, mb: 1 }}>{rationale}</Typography>
      )}

      <Box mb={1}>
        <JsonResultRenderer payload={proposedInput} T={T} />
      </Box>

      {showReject ? (
        <Stack spacing={1}>
          <TextField
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional)"
            size="small"
            multiline
            maxRows={3}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() => reject.mutate()}
              disabled={reject.isPending}
            >
              {reject.isPending ? "Rejecting…" : "Confirm reject"}
            </Button>
            <Button size="small" onClick={() => setShowReject(false)}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            onClick={() => approve.mutate()}
            disabled={approve.isPending || !signature || remaining.expired}
            sx={{ bgcolor: T.confirm, "&:hover": { bgcolor: T.confirm } }}
          >
            {approve.isPending ? "Approving…" : "Approve"}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => setShowReject(true)}
            disabled={approve.isPending}
          >
            Reject
          </Button>
        </Stack>
      )}
    </Box>
  );
}
